import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import NewLanding from "./pages/NewLanding";
import InputForm from "./pages/InputForm";
import AnalysisLoading from "./pages/AnalysisLoading";
import VisibilityReport from "./pages/VisibilityReport";
import EvaluationResultPage from "./pages/EvaluationResultPage";

// Recruiter portal pages
import RecruiterLogin from "./pages/recruiter/RecruiterLogin";
import RecruiterDashboard from "./pages/recruiter/RecruiterDashboard";
import RecruiterOrderDetail from "./pages/recruiter/RecruiterOrderDetail";

// Admin pages
// AdminLogin and AdminDashboard stay statically imported — they're the two
// pages hit immediately after login, so eagerly bundling them means the
// first screen after auth renders with zero chunk-fetch delay. Every other
// admin page is lazy-loaded: none of their ~400KB combined (including the
// 204KB AdminOrder) ships to a visitor who never logs in, and the admin's
// own first paint (login + dashboard) stays fast too.
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
const RecruiterPanel = lazy(() => import("./pages/admin/RecruiterPanel"));
const AdminOrder = lazy(() => import("./pages/admin/AdminOrder"));
const OrdersPage = lazy(() => import("./pages/admin/OrdersPage"));
const InboxPage = lazy(() => import("./pages/admin/InboxPage"));
const AdminFeedbackPage = lazy(() => import("./pages/admin/FeedbackPage"));
const RevenuePage = lazy(() => import("./pages/admin/RevenuePage"));
const PricingPage = lazy(() => import("./pages/admin/PricingPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const AdminActionPlan = lazy(() => import("./pages/admin/AdminActionPlan"));
const MarketingPage = lazy(() => import("./pages/admin/MarketingPage"));

// Shared fallback + wrapper so every lazy admin route doesn't repeat the same
// three lines — keeps the Routes list below readable.
const AdminLazy = ({ children }) => (
  <Suspense fallback={<div style={{ padding: 40, color: '#64748B' }}>Loading…</div>}>
    {children}
  </Suspense>
);

// Legal + public pages
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import CancellationPolicy from "./pages/CancellationPolicy";
import ShippingPolicy from "./pages/ShippingPolicy";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import FeedbackPage from "./pages/FeedbackPage";

// Internal preview pages (hidden, not linked anywhere)
import JourneyAnimationPreview from "./pages/JourneyAnimationPreview";
import BuildingJourneyPreview from "./pages/BuildingJourneyPreview";

// Public pages
import RecruiterReview from "./pages/RecruiterReview";
import CandidateDelivery from "./pages/CandidateDelivery";
import RecruiterOnboarding from "./pages/RecruiterOnboarding";
import IntakeFormPage from "./pages/IntakeFormPage";
import IntakeSuccessPage from "./pages/IntakeSuccessPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutResumePage from "./pages/CheckoutResumePage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";

// UTM campaign attribution — vanity-link landing resolver
import UtmLandingResolver from "./pages/UtmLandingResolver";

// Admin styles
import "./pages/admin/AdminStyles.css";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public routes - Free evaluation flow */}
          <Route path="/" element={<NewLanding />} />
          <Route path="/analyze" element={<InputForm />} />
          <Route path="/loading" element={<AnalysisLoading />} />
          <Route path="/results" element={<VisibilityReport />} />
          <Route path="/evaluation/:id" element={<EvaluationResultPage />} />

          {/* Admin routes - Protected */}
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/recruiters" element={<AdminLazy><RecruiterPanel /></AdminLazy>} />
          <Route path="/admin/orders" element={<AdminLazy><OrdersPage /></AdminLazy>} />
          <Route path="/admin/orders/:orderId" element={<AdminLazy><AdminOrder /></AdminLazy>} />
          {/* Legacy stage-specific pages → unified Orders page with pre-selected filter */}
          <Route path="/admin/order-receipt"   element={<Navigate to="/admin/orders?filter=intake_pending" replace />} />
          <Route path="/admin/in-progress"     element={<Navigate to="/admin/orders?filter=writing" replace />} />
          <Route path="/admin/recruiter-queue" element={<Navigate to="/admin/orders?filter=with_recruiter" replace />} />
          <Route path="/admin/completed"       element={<Navigate to="/admin/orders?filter=delivered" replace />} />
          <Route path="/admin/inbox" element={<AdminLazy><InboxPage /></AdminLazy>} />
          <Route path="/admin/feedback" element={<AdminLazy><AdminFeedbackPage /></AdminLazy>} />
          <Route path="/admin/revenue" element={<AdminLazy><RevenuePage /></AdminLazy>} />
          <Route path="/admin/pricing" element={<AdminLazy><PricingPage /></AdminLazy>} />
          <Route path="/admin/marketing" element={<AdminLazy><MarketingPage /></AdminLazy>} />
          <Route path="/admin/analytics" element={<AdminLazy><AnalyticsPage /></AdminLazy>} />
          <Route path="/admin/action-plan" element={<AdminLazy><AdminActionPlan /></AdminLazy>} />

          {/* Recruiter portal routes */}
          <Route path="/recruiter/login" element={<RecruiterLogin />} />
          <Route path="/recruiter/dashboard" element={<RecruiterDashboard />} />
          <Route path="/recruiter/orders/:orderId" element={<RecruiterOrderDetail />} />

          {/* Public routes - Order flow (after payment) */}
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/resume/:txId" element={<CheckoutResumePage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/intake-form" element={<IntakeFormPage />} />
          <Route path="/intake-success" element={<IntakeSuccessPage />} />
          <Route path="/review/:token" element={<RecruiterReview />} />
          <Route path="/delivery/:token" element={<CandidateDelivery />} />
          <Route path="/recruiter" element={<RecruiterOnboarding />} />

          {/* Legal pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/cancellation" element={<CancellationPolicy />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/feedback" element={<FeedbackPage />} />

          {/* Internal preview routes — hidden, not linked from anywhere */}
          <Route path="/preview/journey-animation" element={<JourneyAnimationPreview />} />
          <Route path="/preview/journey-animation-v2" element={<BuildingJourneyPreview />} />

          {/* UTM campaign vanity links — /{source}/{campaignSlug}/{term}, e.g.
              /instagram/octfest/oct20. Fully-dynamic 3-segment catch-all;
              react-router's static-beats-dynamic route scoring guarantees this
              can never shadow any route above it, regardless of declaration
              order — every existing route has a static literal in a position
              that outscores this one for any URL they'd both match. Placed
              last anyway for readability. See UtmLandingResolver.jsx. */}
          <Route path="/:source/:campaignSlug/:term" element={<UtmLandingResolver />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
